import { useState } from 'react'
import { useTexte } from '../../texte'

import { useDeletePerson, useLoeschvorschau } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { groesse } from '../../lib/format'
import type { Person } from '../../api/types'

/**
 * Ein Konto endgültig wegräumen (#47).
 *
 * Aus dem Issue: „Warnhinweis. Sicherheitsabfrage und Eingabe der E-Mail. nur
 * wenn richtig eingegeben wird auch gelöscht. … Vor dem Löschen eine Liste
 * anzeigen."
 *
 * **Zwei Schritte, und der erste ist keine Zierde.** Erst steht da, was genau
 * verschwindet — gezählt vom Server, nicht geraten —, dann muss die
 * E-Mail-Adresse abgetippt werden. Beides zusammen ist die Rückfrage; eine
 * gewöhnliche „Wirklich?"-Frage wäre hier zu wenig, weil es nichts
 * zurückzuholen gibt. Dieselbe Machart wie `trips/Zuruecksetzen.tsx`.
 *
 * **Die Adresse ist eine Bestätigung, kein Geheimnis.** Wer das hier sieht,
 * ist die Verwaltung und dürfte auch ohne. Sie soll verhindern, dass in einer
 * Liste mit zwanzig Konten das falsche erwischt wird.
 */
export function Kontoloeschung({
  person,
  onGeloescht,
}: {
  person: Person
  /**
   * **Was wegging, wird nach oben gemeldet und nicht hier gezeigt.**
   *
   * Hier stand der Befund selbst, in der Zeile der gelöschten Person — und
   * die Zeile verschwindet im selben Augenblick aus der Liste, weil die
   * Abfrage neu lädt. Wer den Satz zu sehen bekam, entschied damit ein
   * Rennen; der Browserlauf `kontoloeschen.mjs` verlor es zweimal
   * hintereinander und lief in eine Zeitüberschreitung. Eine Meldung, deren
   * Sichtbarkeit vom Zufall abhängt, ist keine.
   */
  onGeloescht: (befund: string) => void
}) {
  const t = useTexte()
  const [offen, setOffen] = useState(false)
  const [getippt, setGetippt] = useState('')
  const vorschau = useLoeschvorschau(person.id, offen)
  const loeschen = useDeletePerson()
  const online = useOnline()

  const zahlen = vorschau.data
  // Groß geschrieben und mit Leerzeichen zählt auch: iOS macht beides von
  // selbst. Der Server vergleicht genauso.
  const passt =
    zahlen !== undefined && getippt.trim().toLowerCase() === zahlen.email.trim().toLowerCase()
  const gesperrt = (zahlen?.eigene.length ?? 0) > 0

  return (
    <div className="stack" style={{ gap: 10 }}>
      {!offen && (
        <button
          type="button"
          className="button button--outline button--gefahr"
          style={{ padding: '6px 12px', fontSize: 14 }}
          onClick={() => {
            setGetippt('')
            setOffen(true)
          }}
        >
          {t.kontoloeschung.oeffnen}
        </button>
      )}

      {offen && (
        <div className="stack" style={{ gap: 12 }}>
          {vorschau.isPending && (
            <p className="feld__hinweis">{t.kontoloeschung.wirdGezaehlt}</p>
          )}

          {vorschau.isError && (
            <p className="notice" role="alert">
              {vorschau.error instanceof Error
                ? vorschau.error.message
                : t.kontoloeschung.uebersichtFehlt}
            </p>
          )}

          {zahlen !== undefined && (
            <>
              {/* **Was verschwindet, in Zahlen.** Erst danach kommt das Feld:
                  wer nicht gelesen hat, was es kostet, soll nicht tippen
                  können. */}
              <div>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                  {t.kontoloeschung.verschwindet}
                </p>
                <ul
                  className="feld__hinweis feld__hinweis--13"
                  style={{ margin: 0, paddingLeft: 20 }}
                >
                  <li>{t.kontoloeschung.geschrieben(zahlen.verfasst)}</li>
                  {/* **Die Zahl, die überrascht.** Sie hängt genauso am
                      Fremdschlüssel — mit dem Konto gehen auch die Worte, die
                      der Reisende ihm geschrieben hat. */}
                  {zahlen.erhalten > 0 && (
                    <li>{t.kontoloeschung.erhalten(zahlen.erhalten)}</li>
                  )}
                  <li>
                    {t.kontoloeschung.dateien(zahlen.dateien)}
                    {zahlen.bytes > 0 && ` · ${groesse(zahlen.bytes)}`}
                  </li>
                  <li>{t.kontoloeschung.reaktionen(zahlen.reaktionen)}</li>
                  <li>{t.kontoloeschung.geraete(zahlen.geraete)}</li>
                </ul>
              </div>

              {zahlen.reisen.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                    {t.kontoloeschung.betroffeneReisen}
                  </p>
                  <ul
                    className="feld__hinweis feld__hinweis--13"
                    style={{ margin: 0, paddingLeft: 20 }}
                  >
                    {zahlen.reisen.map((reise) => (
                      <li key={reise.title}>
                        {t.kontoloeschung.reiseZeile(reise.title, reise.nachrichten)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* **Die eine Sperre, die bleibt.** Sie stünde sonst mitsamt
                  allen Mitlesenden zur Disposition, die niemand gefragt hat.
                  Der Grund steht da, statt dass ein Knopf einfach fehlt. */}
              {gesperrt ? (
                <p className="notice" role="alert" data-hinweis="gesperrt">
                  {t.kontoloeschung.gesperrt(zahlen.eigene.join(', '))}
                </p>
              ) : (
                <>
                  <p className="notice" role="alert" data-hinweis="endgueltig">{t.kontoloeschung.endgueltig}</p>

                  <label className="field">
                    <span className="field__label">
                      {t.kontoloeschung.abtippen} <strong>{zahlen.email}</strong>
                    </span>
                    <input
                      className="field__input"
                      value={getippt}
                      onChange={(event) => setGetippt(event.target.value)}
                      autoCapitalize="off"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      inputMode="email"
                      aria-label={t.kontoloeschung.abtippenName(zahlen.email)}
                      placeholder={zahlen.email}
                    />
                  </label>
                </>
              )}

              {loeschen.isError && (
                <p className="notice" role="alert">
                  {loeschen.error instanceof Error
                    ? loeschen.error.message
                    : t.allgemein.schiefgelaufen}
                </p>
              )}

              {/* Ohne Netz abgeschaltet, mit Grund als Text — ein Tooltip ist
                  auf dem Telefon unsichtbar. */}
              {!online && !gesperrt && (
                <p className="feld__hinweis" role="alert">{t.kontoloeschung.ohneNetz}</p>
              )}

              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="button button--outline button--small"
                  onClick={() => setOffen(false)}
                >
                  {t.kontoloeschung.abbrechen}
                </button>
                {!gesperrt && (
                  <button
                    type="button"
                    className="button button--outline button--small button--gefahr"
                    disabled={!passt || !online || loeschen.isPending}
                    onClick={async () => {
                      const weg = await loeschen.mutateAsync({
                        id: person.id,
                        email: getippt.trim(),
                      })
                      setOffen(false)
                      setGetippt('')
                      // **Gesagt wird, was wirklich wegging** -- die Zahlen der
                      // Antwort und nicht die der Vorschau davor.
                      onGeloescht(
                        t.kontoloeschung.geloescht(zahlen.name, weg.nachrichten, weg.dateien),
                      )
                    }}
                  >
                    {loeschen.isPending ? t.kontoloeschung.wirdGeloescht : t.kontoloeschung.loeschen}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  )
}
